import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getAdminSystemHealth } from "@/api/admin";
import { api } from "@/api/client";
import { Card, SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useToast } from "@/stores/toast";

export default function AdminSystem() {
  const q = useQuery({
    queryKey: ["admin-system"],
    queryFn: getAdminSystemHealth,
    refetchInterval: 30_000,
  });

  if (q.isLoading) return <div className="py-6 text-center text-taupe">Loading…</div>;
  if (!q.data) return <div className="py-6 text-center text-coral">Failed to load.</div>;

  const d = q.data;

  return (
    <div className="space-y-5">
      <Card>
        <div className="flex items-center justify-between">
          <SectionLabel>Scheduler</SectionLabel>
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${
              d.scheduler.running
                ? "bg-amber/15 text-amber-dark"
                : "bg-coral/15 text-coral"
            }`}
          >
            {d.scheduler.running ? "running" : "stopped"}
          </span>
        </div>
        {d.scheduler.error ? (
          <div className="mt-2 rounded-md bg-coral/10 px-3 py-2 text-[12px] text-coral">
            {d.scheduler.error}
          </div>
        ) : null}
        <table className="mt-3 w-full text-[13px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-taupe">
              <th className="py-1">Job</th>
              <th className="py-1">Trigger</th>
              <th className="py-1">Next run</th>
            </tr>
          </thead>
          <tbody>
            {d.scheduler.jobs.length === 0 ? (
              <tr>
                <td colSpan={3} className="py-2 text-taupe">
                  No jobs registered.
                </td>
              </tr>
            ) : (
              d.scheduler.jobs.map((j) => (
                <tr key={j.id} className="border-t border-cream-border">
                  <td className="py-1.5">
                    <div className="font-medium">{j.id}</div>
                    <div className="text-[11px] text-taupe">{j.name}</div>
                  </td>
                  <td className="py-1.5 text-[11px] text-taupe">{j.trigger}</td>
                  <td className="py-1.5 text-taupe">
                    {j.next_run
                      ? new Date(j.next_run).toLocaleString()
                      : "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      <Card>
        <SectionLabel>Recent LLM errors</SectionLabel>
        {d.recent_llm_errors.length === 0 ? (
          <div className="mt-3 text-[13px] text-taupe">
            No errors in the log.
          </div>
        ) : (
          <table className="mt-3 w-full text-[12px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-taupe">
                <th className="py-1">When</th>
                <th className="py-1">User</th>
                <th className="py-1">Model</th>
                <th className="py-1">Op</th>
                <th className="py-1">Error</th>
              </tr>
            </thead>
            <tbody>
              {d.recent_llm_errors.map((e) => (
                <tr key={e.id} className="border-t border-cream-border">
                  <td className="py-1.5 text-taupe">
                    {e.created_at
                      ? new Date(e.created_at).toLocaleString()
                      : "—"}
                  </td>
                  <td className="py-1.5 text-taupe">
                    {e.user_id ?? "—"}
                  </td>
                  <td className="py-1.5 text-[11px] text-taupe">
                    {e.model}
                  </td>
                  <td className="py-1.5 text-[11px] text-taupe">
                    {e.operation}
                  </td>
                  <td className="py-1.5 text-coral">{e.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card>
        <SectionLabel>Database</SectionLabel>
        <div className="mt-2 text-[14px]">
          SQLite file size:{" "}
          <span className="metric-number text-[18px]">{d.db_size_mb}</span>{" "}
          MB
        </div>
      </Card>

      <OpenAIIntegrationSection />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Phase 24.8a — OpenAI gpt-image-2 admin integration
// ---------------------------------------------------------------------------

interface OpenAIKeyStatus {
  key_set: boolean;
  last_test_status: "never" | "ok" | "failed";
  last_test_at: string | null;
  last_test_error: string | null;
  set_at: string | null;
}

function OpenAIIntegrationSection() {
  const qc = useQueryClient();
  const toast = useToast();
  const [showInput, setShowInput] = useState(false);
  const [newKey, setNewKey] = useState("");

  const statusQ = useQuery<OpenAIKeyStatus>({
    queryKey: ["admin", "openai-status"],
    queryFn: async () => {
      const { data } = await api.get<OpenAIKeyStatus>(
        "/admin/integrations/openai-image/status",
      );
      return data;
    },
    refetchInterval: 60_000,
  });

  const setKeyMut = useMutation({
    mutationFn: async (key: string) => {
      const { data } = await api.post(
        "/admin/integrations/openai-image/key",
        { api_key: key },
      );
      return data;
    },
    onSuccess: (data) => {
      toast.show({
        message: data.ok
          ? "API key saved and tested successfully"
          : `API key saved but test failed: ${data.test_error || "unknown error"}`,
        variant: data.ok ? "success" : "error",
      });
      qc.invalidateQueries({ queryKey: ["admin", "openai-status"] });
      setNewKey("");
      setShowInput(false);
    },
    onError: (err: unknown) => {
      const detail =
        err && typeof err === "object" && "response" in err
          ? ((err as { response?: { data?: { detail?: string } } }).response
              ?.data?.detail ?? "Failed to save API key")
          : "Failed to save API key";
      toast.show({ message: detail, variant: "error" });
    },
  });

  const testKeyMut = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(
        "/admin/integrations/openai-image/test",
      );
      return data;
    },
    onSuccess: (data) => {
      toast.show({
        message: data.ok ? "Connection test passed" : `Test failed: ${data.error || "unknown"}`,
        variant: data.ok ? "success" : "error",
      });
      qc.invalidateQueries({ queryKey: ["admin", "openai-status"] });
    },
  });

  const clearKeyMut = useMutation({
    mutationFn: async () => {
      const { data } = await api.delete(
        "/admin/integrations/openai-image/key",
      );
      return data;
    },
    onSuccess: () => {
      toast.show({ message: "API key removed", variant: "success" });
      qc.invalidateQueries({ queryKey: ["admin", "openai-status"] });
    },
  });

  const status = statusQ.data;

  return (
    <Card data-testid="openai-integration-section">
      <SectionLabel>OpenAI gpt-image-2 (recipe photos)</SectionLabel>
      <div className="mt-1 text-[12px] text-taupe">
        API key for generating recipe photos via gpt-image-2 (~$0.053/image medium quality).
      </div>

      {statusQ.isLoading ? (
        <div className="mt-3 text-[12px] text-taupe">Loading…</div>
      ) : status?.key_set ? (
        <div className="mt-3 space-y-1 text-[13px]">
          <div className="flex items-center gap-2">
            <span className="text-taupe">Status:</span>
            <span
              className={
                status.last_test_status === "ok"
                  ? "font-medium text-emerald-600"
                  : status.last_test_status === "failed"
                  ? "font-medium text-coral"
                  : "text-taupe"
              }
            >
              {status.last_test_status === "ok"
                ? "Working"
                : status.last_test_status === "failed"
                ? "Failed"
                : "Never tested"}
            </span>
          </div>
          {status.last_test_at ? (
            <div className="text-[11px] text-taupe">
              Last tested: {new Date(status.last_test_at).toLocaleString()}
            </div>
          ) : null}
          {status.last_test_error ? (
            <div className="rounded-md bg-coral/10 px-2 py-1 text-[11px] text-coral">
              Error: {status.last_test_error}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="mt-3 text-[13px] text-taupe">No API key set.</div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {!showInput ? (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowInput(true)}
              data-testid="openai-set-key-button"
            >
              {status?.key_set ? "Replace key" : "Set API key"}
            </Button>
            {status?.key_set ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => testKeyMut.mutate()}
                  loading={testKeyMut.isPending}
                  data-testid="openai-test-key-button"
                >
                  Test connection
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (window.confirm("Remove OpenAI API key? Image generation will be disabled.")) {
                      clearKeyMut.mutate();
                    }
                  }}
                  loading={clearKeyMut.isPending}
                  data-testid="openai-clear-key-button"
                >
                  Remove key
                </Button>
              </>
            ) : null}
          </>
        ) : (
          <div className="w-full space-y-2">
            <Input
              type="password"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder="sk-..."
              label="OpenAI API key"
              hint="Format: sk-... (from console.openai.com)"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => setKeyMut.mutate(newKey.trim())}
                disabled={!newKey.startsWith("sk-") || setKeyMut.isPending}
                loading={setKeyMut.isPending}
                data-testid="openai-save-key-button"
              >
                Save and test
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowInput(false);
                  setNewKey("");
                }}
                disabled={setKeyMut.isPending}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
